import { useMemo, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { isBitSet, longDateString } from "../../utils";
import "../../starek.css";

const DAYS_OF_WEEK = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

const formatTime = (sec) => {
    if (sec == null) return "-";
    const d = new Date(0);
    d.setSeconds(sec);
    let h = d.getUTCHours();
    const m = d.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
};

const runsOnToDays = (mask) => {
    const days = [];
    DAYS_OF_WEEK.forEach((day, i) => {
        if (isBitSet(mask, i)) days.push(day.slice(0, 3));
    });
    return days.length ? days.join(", ") : "-";
};

function ShiftListingView({
    items,
    selectedRows,
    onSelectedRowsChange,
    filterText,
    toggleCleared,
    noDataComponent,
    onRowClicked,
}) {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const filteredItems = useMemo(() => {
        const q = filterText?.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [items, filterText]);

    const paginatedItems = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredItems.slice(start, start + rowsPerPage);
    }, [filteredItems, page, rowsPerPage]);

    useEffect(() => {
        setPage(1);
    }, [filterText]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handlePerRowsChange = (newPerPage, newPage) => {
        setRowsPerPage(newPerPage);
        setPage(newPage);
    };

    const isShiftSelected = (shiftId) => {
        return selectedRows.some((row) => row.id === shiftId);
    };

    const handleCheckboxChange = (shift) => {
        let newSelectedRows;
        if (isShiftSelected(shift.id)) {
            newSelectedRows = selectedRows.filter((row) => row.id !== shift.id);
        } else {
            newSelectedRows = [...selectedRows, shift];
        }
        if (onSelectedRowsChange) {
            onSelectedRowsChange({ selectedRows: newSelectedRows });
        }
    };

    const columns = [
        {
            name: (
                <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    onChange={(e) => {
                        if (e.target.checked) {
                            const newRows = [
                                ...selectedRows,
                                ...paginatedItems.filter(
                                    (r) => !isShiftSelected(r.id),
                                ),
                            ];
                            if (onSelectedRowsChange) {
                                onSelectedRowsChange({ selectedRows: newRows });
                            }
                        } else {
                            const pageIds = new Set(
                                paginatedItems.map((r) => r.id),
                            );
                            if (onSelectedRowsChange) {
                                onSelectedRowsChange({
                                    selectedRows: selectedRows.filter(
                                        (r) => !pageIds.has(r.id),
                                    ),
                                });
                            }
                        }
                    }}
                    checked={
                        paginatedItems.length > 0 &&
                        paginatedItems.every((row) => isShiftSelected(row.id))
                    }
                    ref={(el) => {
                        if (!el) return;
                        const some = paginatedItems.some((r) =>
                            isShiftSelected(r.id),
                        );
                        const all =
                            paginatedItems.length > 0 &&
                            paginatedItems.every((r) => isShiftSelected(r.id));
                        el.indeterminate = some && !all;
                    }}
                />
            ),
            width: "60px",
            center: true,
            sortable: false,
            cell: (row) => (
                <input
                    type="checkbox"
                    checked={isShiftSelected(row.id)}
                    onChange={() => handleCheckboxChange(row)}
                    aria-label={`Select ${row.name}`}
                />
            ),
            ignoreRowClick: true,
        },
        {
            name: "Name",
            selector: (r) => r.name || "-",
            sortable: true,
            height: "50px",
            grow: 1.2,
        },
        {
            name: "Description",
            selector: (r) => r.description || "-",
            sortable: true,
            height: "50px",
            grow: 2,
            cell: (r) => (
                <div
                    className="text-truncate h-50px"
                    style={{ maxWidth: 250 }}
                >
                    {r.description || "-"}
                </div>
            ),
        },
        {
            name: "Timings",
            selector: (r) =>
                `${formatTime(r.start_time)} - ${formatTime(r.end_time)}`,
            height: "50px",
            width: "180px",
            sortable: true,
        },
        {
            name: "Days",
            selector: (r) => runsOnToDays(r.runs_on),
            height: "50px",
            grow: 1,
        },
        {
            name: "Activity",
            selector: (r) => r.updated_at,
            height: "50px",
            width: "250px",
            sortable: true,
            cell: (r) => {
                const tooltipText = `Created by ${
                    r.created_by?.name || "Unknown"
                } at ${
                    r.created_at ? longDateString(r.created_at) : "-"
                }\nModified by ${r.updated_by?.name || "Unknown"} at ${
                    r.updated_at ? longDateString(r.updated_at) : "-"
                }`;
                return (
                    <div
                        style={{ fontSize: "0.85rem" }}
                        title={tooltipText}
                    >
                        {r.updated_by?.name || "Unknown"} at{" "}
                        {r.updated_at ? longDateString(r.updated_at) : "-"}
                    </div>
                );
            },
        },
        {
            name: "Status",
            width: "90px",
            cell: (r) => (
                <span className={`badge bg-${r.is_active ? "success" : "secondary"}`}>
                    {r.is_active ? "Active" : "Inactive"}
                </span>
            ),
        },
    ];

    const conditionalRowStyles = [
        { when: (r) => !r.is_active, style: { opacity: 0.5, backgroundColor: "#f8f9fa" } },
    ];

    return (
        <DataTable
            columns={columns}
            data={paginatedItems}
            highlightOnHover
            striped
            pointerOnHover
            onRowClicked={(row) => onRowClicked?.(row)}
            responsive
            pagination
            paginationServer
            paginationTotalRows={filteredItems.length}
            paginationDefaultPage={page}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            dense
            noDataComponent={noDataComponent}
            conditionalRowStyles={conditionalRowStyles}
        />
    );
}

export default ShiftListingView;
